import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { identityGateway } from '../services';
import { AuthShellLayout } from '../components/AuthShellLayout';
import type { Slide } from '../components/HeroCarousel';
import { TextField } from '../../../shared/components/TextField';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { friendlyErrorMessage } from '../../../shared/friendlyError';

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Backend's own enumeration-safe copy (`PasswordRecoveryEndpoints.ForgotPasswordAcceptedMessage`) —
 * shown regardless of whether the email exists, so this page never hints at that either. */
const ACCEPTED_MESSAGE = "If that email exists, we've sent password reset instructions.";

export interface ForgotPasswordPageProps {
  /** EPN-36 Partner Sign In overrides — every default reproduces Admin's own current copy/behavior
   * exactly; only PartnerForgotPasswordPage passes anything else. The actual
   * `POST /api/auth/password/forgot` call is identical either way. */
  heroSlides?: Slide[] | undefined;
  signInHref?: string;
  heading?: string;
  bodyCopy?: string;
  submitLabel?: string;
}

/**
 * The Login Security counterpart to LoginPage's now-real "Forgot your Password?" link. Always shows the
 * same success message on submit — never a distinguishing state for "no such account" — matching
 * `POST /api/auth/password/forgot`'s always-200 contract.
 */
export function ForgotPasswordPage({
  heroSlides,
  signInHref = '/login',
  heading = 'Forgot Your Password?',
  bodyCopy = "Enter your email address and we'll send you instructions to reset your password.",
  submitLabel = 'Send Reset Link',
}: ForgotPasswordPageProps) {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFieldError('Enter your email address.');
      return;
    }
    if (!EMAIL_FORMAT.test(trimmedEmail)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    try {
      await identityGateway.forgotPassword(trimmedEmail);
      // Always shown on a successful call regardless of what the backend returned — never let a
      // response detail leak an enumeration signal into this page.
      setSubmitted(true);
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err, "We couldn't send that right now."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShellLayout heroSlides={heroSlides}>
      <div className="auth-shell__form-slot">
        <h1>{heading}</h1>
        {submitted ? (
          <>
            <InlineAlert variant="success" data-testid="forgot-password-success-message">
              {ACCEPTED_MESSAGE}
            </InlineAlert>
            <p className="auth-shell__secondary-action">
              <Link to={signInHref} className="auth-shell__inline-link">
                Back to Sign In
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <p className="muted">{bodyCopy}</p>

            <TextField
              label="Email Address"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              errorMessage={fieldError}
              data-testid="forgot-password-email-input"
              autoFocus
            />

            {submitError && (
              <InlineAlert variant="error" data-testid="forgot-password-error-message">
                {submitError}
              </InlineAlert>
            )}

            <div className="auth-shell__submit-row">
              <Button
                type="submit"
                loading={submitting}
                loadingLabel="Sending…"
                className="auth-submit-button"
                data-testid="forgot-password-submit-button"
              >
                {submitLabel}
              </Button>
              <p className="auth-shell__secondary-action">
                <Link to={signInHref} className="auth-shell__inline-link">
                  Back to Sign In
                </Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </AuthShellLayout>
  );
}
